import * as React from "react";

import classNames from "classnames";

import GenButton from "./Button";

interface GenS3UploaderButtonProps {
  name: string;
  accept?: string;
  filePath: string;
  objectName: string | ((selectedFileName: string) => string);
  onError?: (error: string) => void;
  onFinish?: (url: string) => void;
  titleEnding?: string;

}

export default function GenS3UploaderButton(props: GenS3UploaderButtonProps) {
  const {
    name="",
    accept,
    onError,
    onFinish,
    filePath,
    objectName,
    titleEnding="",
  } = props;

  const id = React.useId();

  const [progressPercent, setProgressPercent] = React.useState<number>();

  const onProgress = React.useCallback((progressEvent: number) => {
    setProgressPercent(progressEvent);
  }, [setProgressPercent]);

  const onUploadError = React.useCallback((error: string) => {
    console.log("ERROR:", error);
    alert("Error Uploading");

    if (onError) { onError(error); }
    setProgressPercent(undefined);
  }, [onError, setProgressPercent]);

  const onUploadFinish = React.useCallback((signedUrl: string) => {
    if (onFinish) { onFinish(signedUrl); }
    setProgressPercent(undefined);
  }, [onFinish, setProgressPercent]);


  const handleOnChange = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) { return; }

    const file = fileList.item(0);
    if (!file) { return; }

    try {
      const scrubedName = file.name.replace(/[^\w\d_\-\.]+/ig, "");

      const queryArguments = {
        path: filePath,
        objectName: typeof objectName === "string" ? objectName : objectName(scrubedName),
        contentType: file.type,
      };

      const signedUrlResponse = await fetch(`/media_assets/signed_url?${new URLSearchParams(queryArguments)}`);

      if (!signedUrlResponse.ok) throw new Error("Failed to get signed URL");

      const { signedUrl } = await signedUrlResponse.json();

      const xhr = new XMLHttpRequest();
      xhr.open("PUT", signedUrl);

      xhr.upload.addEventListener("progress", function (e) {
        if (e.lengthComputable) {
          const percent = (e.loaded / e.total) * 100;
          if (onProgress) onProgress(percent);
        }
      });

      xhr.onerror = function () {
        throw new Error("Upload failed");
      };

      xhr.onload = function () {
        if (xhr.status === 200) {
          onUploadFinish(signedUrl);
        } else {
          throw new Error(`Upload Error: S3 responsed with ${xhr.status}`);
        }
      };

      xhr.setRequestHeader("Content-Type", file.type);

      xhr.send(file);
    } catch (error) {
      if (error instanceof Error) {
        onUploadError(error.message);
      } else {
        onUploadError("Upload Error");
      }
    }
  }, [onProgress, onUploadError, onUploadFinish]);

  const hasProgress = typeof(progressPercent) === "number";

  return (
    <GenButton
      name={(
        <>
          <label htmlFor={id} className={classNames("font-normal", {disabled: hasProgress})}>
            {hasProgress ? "Uploading" : name}

            <input
              id={id}
              type='file'
              accept={accept}
              onChange={(e) => handleOnChange(e).then(() => {})}
              style={{display: "none"}}
            />
          </label>
          {hasProgress && (
            <div className="progress progress-striped active">
              <div className="bar" style={{width: `${progressPercent * 0.95}%`}} />
            </div>
          )}
        </>
      )}
      highlighted
      small
      disabled={hasProgress}
    />
  );
}
